import { create } from 'zustand';
import apiClient from '../api/client';

export type ScrollSourceId = 'x' | 'instagram';
export type ScrollMediaType = 'text' | 'image' | 'video' | 'mixed';
export type ScrollSessionStatus = 'disabled' | 'not_connected' | 'connected' | 'stale';
export type ScrollDecision = 'show' | 'downrank' | 'hide';
export type ScrollScorerState = 'configured' | 'unconfigured' | 'degraded';

export interface ScrollPromptProfile {
  prompt: string;
  updated_at: string | null;
}

export interface ScrollSourceConfig {
  enabled: boolean;
}

export interface ScrollConfig {
  prompt_profile: ScrollPromptProfile;
  sources: Record<ScrollSourceId, ScrollSourceConfig>;
}

export interface ScrollVideoPreview {
  preview_url: string | null;
  expanded_url: string | null;
  poster_url: string | null;
  duration_seconds: number | null;
  muted_preview: boolean;
}

export interface ScrollBrowserStreamSession {
  session_id: string;
  source: ScrollSourceId;
  stream_url: string;
  created_at: string;
  expires_at: string;
  viewport_width: number;
  viewport_height: number;
  current_url: string | null;
  page_title: string | null;
}

export interface ScrollVideoBrowserSession {
  session_id: string;
  item_id: string;
  source: ScrollSourceId;
  stream_url: string;
  created_at: string;
  expires_at: string;
  source_url: string;
  current_url: string | null;
  page_title: string | null;
  viewport_width: number;
  viewport_height: number;
  capture: ScrollVideoCaptureSession;
}

export interface ScrollVideoCaptureSession {
  status: 'warming' | 'capturing' | 'ready' | 'failed';
  capture_url: string | null;
  content_type: string | null;
  bytes_captured: number;
  timeline_start_seconds: number | null;
  ready_at: string | null;
  last_error: string | null;
  renewing: boolean;
  renewal_bytes_captured: number;
}

export interface ScrollFeedItem {
  id: string;
  source: ScrollSourceId;
  author_name: string;
  caption: string;
  source_url: string;
  published_at: string | null;
  media_type: ScrollMediaType;
  preview_image_url: string | null;
  video: ScrollVideoPreview | null;
  interest_score: number | null;
  decision: ScrollDecision | null;
  reason: string | null;
}

export interface ScrollSessionMaterialSummary {
  persisted: boolean;
  updated_at: string | null;
  login_hint: string | null;
  user_agent: string | null;
  cookie_count: number;
  origin_count: number;
}

export interface ScrollSourceState {
  source: ScrollSourceId;
  label: string;
  enabled: boolean;
  collector_mode: 'live_session';
  session_status: ScrollSessionStatus;
  last_collected_at: string | null;
  status_detail: string;
  runtime_started_at: string | null;
  current_url: string | null;
  page_title: string | null;
  session_material: ScrollSessionMaterialSummary;
  browser_stream: ScrollBrowserStreamSession | null;
}

export interface ScrollScorerStatus {
  state: ScrollScorerState;
  detail: string;
  last_error: string | null;
  last_error_at: string | null;
  last_refresh_at: string | null;
  last_ranked_at: string | null;
  last_attempted_item_count: number;
  last_ranked_item_count: number;
  last_fallback_item_count: number;
  latest_refresh_used_fallback: boolean;
  recent_refreshes: ScrollScorerRefresh[];
}

export interface ScrollScorerRefresh {
  refreshed_at: string;
  state: ScrollScorerState;
  detail: string;
  attempted_item_count: number;
  ranked_item_count: number;
  fallback_item_count: number;
  used_fallback: boolean;
  last_error: string | null;
  ranked_at: string | null;
}

export interface ScrollStateResponse {
  config: ScrollConfig;
  sources: ScrollSourceState[];
  ranking_status: ScrollScorerStatus;
  items: ScrollFeedItem[];
}

export interface ScrollConfigUpdateRequest {
  prompt_profile?: {
    prompt?: string;
  };
  sources?: Partial<Record<ScrollSourceId, { enabled?: boolean }>>;
}

export interface ScrollSourceSessionUpsertRequest {
  storage_state: {
    cookies: Record<string, unknown>[];
    origins: Record<string, unknown>[];
  };
  login_hint?: string;
  user_agent?: string;
}

interface ScrollState {
  config: ScrollConfig | null;
  sources: ScrollSourceState[];
  rankingStatus: ScrollScorerStatus | null;
  items: ScrollFeedItem[];
  videoSession: ScrollVideoBrowserSession | null;
  videoBusy: boolean;
  loading: boolean;
  saving: boolean;
  refreshing: boolean;
  sourceBusy: Record<ScrollSourceId, boolean>;
  error: string | null;
  fetchState: () => Promise<void>;
  saveConfig: (updates: ScrollConfigUpdateRequest) => Promise<void>;
  refreshFeed: () => Promise<void>;
  saveSourceSession: (source: ScrollSourceId, payload: ScrollSourceSessionUpsertRequest) => Promise<void>;
  saveLiveSourceSession: (source: ScrollSourceId, loginHint?: string) => Promise<void>;
  connectSourceRuntime: (source: ScrollSourceId) => Promise<void>;
  openBrowserSession: (source: ScrollSourceId) => Promise<void>;
  closeBrowserSession: (source: ScrollSourceId) => Promise<void>;
  browserNavigate: (source: ScrollSourceId, sessionId: string, url: string) => Promise<void>;
  browserBack: (source: ScrollSourceId, sessionId: string) => Promise<void>;
  browserForward: (source: ScrollSourceId, sessionId: string) => Promise<void>;
  browserReload: (source: ScrollSourceId, sessionId: string) => Promise<void>;
  browserClick: (source: ScrollSourceId, sessionId: string, x: number, y: number) => Promise<void>;
  browserType: (
    source: ScrollSourceId,
    sessionId: string,
    text: string,
    replaceExisting?: boolean,
  ) => Promise<void>;
  browserPress: (source: ScrollSourceId, sessionId: string, key: string) => Promise<void>;
  browserScroll: (source: ScrollSourceId, sessionId: string, deltaX: number, deltaY: number) => Promise<void>;
  browserDrag: (
    source: ScrollSourceId,
    sessionId: string,
    startX: number,
    startY: number,
    endX: number,
    endY: number,
  ) => Promise<void>;
  openVideoSession: (itemId: string) => Promise<void>;
  refreshVideoSession: (sessionId: string) => Promise<void>;
  closeVideoSession: () => Promise<void>;
  videoSessionClick: (sessionId: string, x: number, y: number) => Promise<void>;
  videoSessionScroll: (sessionId: string, deltaX: number, deltaY: number) => Promise<void>;
  clearSourceSession: (source: ScrollSourceId) => Promise<void>;
}

export const useScrollStore = create<ScrollState>((set, get) => ({
  config: null,
  sources: [],
  rankingStatus: null,
  items: [],
  videoSession: null,
  videoBusy: false,
  loading: false,
  saving: false,
  refreshing: false,
  sourceBusy: { x: false, instagram: false },
  error: null,

  fetchState: async () => {
    set({ loading: true, error: null });
    try {
      const state = await apiClient.get<ScrollStateResponse>('/api/scroll/state');
        set({
          config: state.config,
          sources: state.sources,
          rankingStatus: state.ranking_status,
          items: state.items,
          loading: false,
        });
    } catch (err) {
      set({ error: (err as Error).message, loading: false });
    }
  },

  saveConfig: async (updates) => {
    set({ saving: true, error: null });
    try {
      const state = await apiClient.put<ScrollStateResponse>('/api/scroll/config', updates);
        set({
          config: state.config,
          sources: state.sources,
          rankingStatus: state.ranking_status,
          items: state.items,
          saving: false,
        });
    } catch (err) {
      set({ error: (err as Error).message, saving: false });
    }
  },

  refreshFeed: async () => {
    set({ refreshing: true, error: null });
    try {
      const state = await apiClient.post<ScrollStateResponse>('/api/scroll/refresh');
        set({
          config: state.config,
          sources: state.sources,
          rankingStatus: state.ranking_status,
          items: state.items,
          refreshing: false,
        });
    } catch (err) {
      set({ error: (err as Error).message, refreshing: false });
    }
  },

  saveSourceSession: async (source, payload) => {
    set((state) => ({
      error: null,
      sourceBusy: { ...state.sourceBusy, [source]: true },
    }));
    try {
      const state = await apiClient.post<ScrollStateResponse>(`/api/scroll/sources/${source}/session`, payload);
        set((current) => ({
          config: state.config,
          sources: state.sources,
          rankingStatus: state.ranking_status,
          items: state.items,
          sourceBusy: { ...current.sourceBusy, [source]: false },
        }));
    } catch (err) {
      set((current) => ({
        error: (err as Error).message,
        sourceBusy: { ...current.sourceBusy, [source]: false },
      }));
    }
  },

  saveLiveSourceSession: async (source, loginHint) => {
    set((state) => ({
      error: null,
      sourceBusy: { ...state.sourceBusy, [source]: true },
    }));
    try {
      const state = await apiClient.post<ScrollStateResponse>(`/api/scroll/sources/${source}/session/save-live`, {
        login_hint: loginHint || undefined,
      });
      set((current) => ({
        config: state.config,
        sources: state.sources,
        rankingStatus: state.ranking_status,
        items: state.items,
        sourceBusy: { ...current.sourceBusy, [source]: false },
      }));
    } catch (err) {
      set((current) => ({
        error: (err as Error).message,
        sourceBusy: { ...current.sourceBusy, [source]: false },
      }));
    }
  },

  connectSourceRuntime: async (source) => {
    set((state) => ({
      error: null,
      sourceBusy: { ...state.sourceBusy, [source]: true },
    }));
    try {
      const state = await apiClient.post<ScrollStateResponse>(`/api/scroll/sources/${source}/runtime/connect`);
        set((current) => ({
          config: state.config,
          sources: state.sources,
          rankingStatus: state.ranking_status,
          items: state.items,
          sourceBusy: { ...current.sourceBusy, [source]: false },
        }));
    } catch (err) {
      set((current) => ({
        error: (err as Error).message,
        sourceBusy: { ...current.sourceBusy, [source]: false },
      }));
    }
  },

  openBrowserSession: async (source) => {
    set((state) => ({
      error: null,
      sourceBusy: { ...state.sourceBusy, [source]: true },
    }));
    try {
      const state = await apiClient.post<ScrollStateResponse>(`/api/scroll/sources/${source}/browser/session`);
      set((current) => ({
        config: state.config,
        sources: state.sources,
        rankingStatus: state.ranking_status,
        items: state.items,
        sourceBusy: { ...current.sourceBusy, [source]: false },
      }));
    } catch (err) {
      set((current) => ({
        error: (err as Error).message,
        sourceBusy: { ...current.sourceBusy, [source]: false },
      }));
    }
  },

  closeBrowserSession: async (source) => {
    set((state) => ({
      error: null,
      sourceBusy: { ...state.sourceBusy, [source]: true },
    }));
    try {
      const state = await apiClient.delete<ScrollStateResponse>(`/api/scroll/sources/${source}/browser/session`);
      set((current) => ({
        config: state.config,
        sources: state.sources,
        rankingStatus: state.ranking_status,
        items: state.items,
        sourceBusy: { ...current.sourceBusy, [source]: false },
      }));
    } catch (err) {
      set((current) => ({
        error: (err as Error).message,
        sourceBusy: { ...current.sourceBusy, [source]: false },
      }));
    }
  },

  browserNavigate: async (source, sessionId, url) => {
    set((state) => ({
      error: null,
      sourceBusy: { ...state.sourceBusy, [source]: true },
    }));
    try {
      const browserStream = await apiClient.post<ScrollBrowserStreamSession>(
        `/api/scroll/streams/browser/${sessionId}/navigate`,
        { url },
      );
      set((current) => ({
        sources: current.sources.map((item) =>
          item.source === source
            ? {
                ...item,
                current_url: browserStream.current_url,
                page_title: browserStream.page_title,
                browser_stream: browserStream,
              }
            : item,
        ),
        sourceBusy: { ...current.sourceBusy, [source]: false },
      }));
    } catch (err) {
      set((current) => ({
        error: (err as Error).message,
        sourceBusy: { ...current.sourceBusy, [source]: false },
      }));
    }
  },

  browserClick: async (source, sessionId, x, y) => {
    set((state) => ({
      error: null,
      sourceBusy: { ...state.sourceBusy, [source]: true },
    }));
    try {
      const browserStream = await apiClient.post<ScrollBrowserStreamSession>(
        `/api/scroll/streams/browser/${sessionId}/click`,
        { x, y },
      );
      set((current) => ({
        sources: current.sources.map((item) =>
          item.source === source
            ? {
                ...item,
                current_url: browserStream.current_url,
                page_title: browserStream.page_title,
                browser_stream: browserStream,
              }
            : item,
        ),
        sourceBusy: { ...current.sourceBusy, [source]: false },
      }));
    } catch (err) {
      set((current) => ({
        error: (err as Error).message,
        sourceBusy: { ...current.sourceBusy, [source]: false },
      }));
    }
  },

  browserBack: async (source, sessionId) => {
    set((state) => ({
      error: null,
      sourceBusy: { ...state.sourceBusy, [source]: true },
    }));
    try {
      const browserStream = await apiClient.post<ScrollBrowserStreamSession>(
        `/api/scroll/streams/browser/${sessionId}/back`,
      );
      set((current) => ({
        sources: current.sources.map((item) =>
          item.source === source
            ? {
                ...item,
                current_url: browserStream.current_url,
                page_title: browserStream.page_title,
                browser_stream: browserStream,
              }
            : item,
        ),
        sourceBusy: { ...current.sourceBusy, [source]: false },
      }));
    } catch (err) {
      set((current) => ({
        error: (err as Error).message,
        sourceBusy: { ...current.sourceBusy, [source]: false },
      }));
    }
  },

  browserForward: async (source, sessionId) => {
    set((state) => ({
      error: null,
      sourceBusy: { ...state.sourceBusy, [source]: true },
    }));
    try {
      const browserStream = await apiClient.post<ScrollBrowserStreamSession>(
        `/api/scroll/streams/browser/${sessionId}/forward`,
      );
      set((current) => ({
        sources: current.sources.map((item) =>
          item.source === source
            ? {
                ...item,
                current_url: browserStream.current_url,
                page_title: browserStream.page_title,
                browser_stream: browserStream,
              }
            : item,
        ),
        sourceBusy: { ...current.sourceBusy, [source]: false },
      }));
    } catch (err) {
      set((current) => ({
        error: (err as Error).message,
        sourceBusy: { ...current.sourceBusy, [source]: false },
      }));
    }
  },

  browserReload: async (source, sessionId) => {
    set((state) => ({
      error: null,
      sourceBusy: { ...state.sourceBusy, [source]: true },
    }));
    try {
      const browserStream = await apiClient.post<ScrollBrowserStreamSession>(
        `/api/scroll/streams/browser/${sessionId}/reload`,
      );
      set((current) => ({
        sources: current.sources.map((item) =>
          item.source === source
            ? {
                ...item,
                current_url: browserStream.current_url,
                page_title: browserStream.page_title,
                browser_stream: browserStream,
              }
            : item,
        ),
        sourceBusy: { ...current.sourceBusy, [source]: false },
      }));
    } catch (err) {
      set((current) => ({
        error: (err as Error).message,
        sourceBusy: { ...current.sourceBusy, [source]: false },
      }));
    }
  },

  browserType: async (source, sessionId, text, replaceExisting = false) => {
    set((state) => ({
      error: null,
      sourceBusy: { ...state.sourceBusy, [source]: true },
    }));
    try {
      const browserStream = await apiClient.post<ScrollBrowserStreamSession>(
        `/api/scroll/streams/browser/${sessionId}/type`,
        { text, replace_existing: replaceExisting },
      );
      set((current) => ({
        sources: current.sources.map((item) =>
          item.source === source
            ? {
                ...item,
                current_url: browserStream.current_url,
                page_title: browserStream.page_title,
                browser_stream: browserStream,
              }
            : item,
        ),
        sourceBusy: { ...current.sourceBusy, [source]: false },
      }));
    } catch (err) {
      set((current) => ({
        error: (err as Error).message,
        sourceBusy: { ...current.sourceBusy, [source]: false },
      }));
    }
  },

  browserScroll: async (source, sessionId, deltaX, deltaY) => {
    set((state) => ({
      error: null,
      sourceBusy: { ...state.sourceBusy, [source]: true },
    }));
    try {
      const browserStream = await apiClient.post<ScrollBrowserStreamSession>(
        `/api/scroll/streams/browser/${sessionId}/scroll`,
        { delta_x: deltaX, delta_y: deltaY },
      );
      set((current) => ({
        sources: current.sources.map((item) =>
          item.source === source
            ? {
                ...item,
                current_url: browserStream.current_url,
                page_title: browserStream.page_title,
                browser_stream: browserStream,
              }
            : item,
        ),
        sourceBusy: { ...current.sourceBusy, [source]: false },
      }));
    } catch (err) {
      set((current) => ({
        error: (err as Error).message,
        sourceBusy: { ...current.sourceBusy, [source]: false },
      }));
    }
  },

  browserDrag: async (source, sessionId, startX, startY, endX, endY) => {
    set((state) => ({
      error: null,
      sourceBusy: { ...state.sourceBusy, [source]: true },
    }));
    try {
      const browserStream = await apiClient.post<ScrollBrowserStreamSession>(
        `/api/scroll/streams/browser/${sessionId}/drag`,
        { start_x: startX, start_y: startY, end_x: endX, end_y: endY },
      );
      set((current) => ({
        sources: current.sources.map((item) =>
          item.source === source
            ? {
                ...item,
                current_url: browserStream.current_url,
                page_title: browserStream.page_title,
                browser_stream: browserStream,
              }
            : item,
        ),
        sourceBusy: { ...current.sourceBusy, [source]: false },
      }));
    } catch (err) {
      set((current) => ({
        error: (err as Error).message,
        sourceBusy: { ...current.sourceBusy, [source]: false },
      }));
    }
  },

  browserPress: async (source, sessionId, key) => {
    set((state) => ({
      error: null,
      sourceBusy: { ...state.sourceBusy, [source]: true },
    }));
    try {
      const browserStream = await apiClient.post<ScrollBrowserStreamSession>(
        `/api/scroll/streams/browser/${sessionId}/press`,
        { key },
      );
      set((current) => ({
        sources: current.sources.map((item) =>
          item.source === source
            ? {
                ...item,
                current_url: browserStream.current_url,
                page_title: browserStream.page_title,
                browser_stream: browserStream,
              }
            : item,
        ),
        sourceBusy: { ...current.sourceBusy, [source]: false },
      }));
    } catch (err) {
      set((current) => ({
        error: (err as Error).message,
        sourceBusy: { ...current.sourceBusy, [source]: false },
      }));
    }
  },

  openVideoSession: async (itemId) => {
    set({ error: null, videoBusy: true });
    const currentSessionId = get().videoSession?.session_id;
    try {
      if (currentSessionId) {
        await apiClient.delete(`/api/scroll/video-sessions/${currentSessionId}`);
      }
      const session = await apiClient.post<ScrollVideoBrowserSession>(
        `/api/scroll/items/${encodeURIComponent(itemId)}/video/session`,
      );
      set({ videoSession: session, videoBusy: false });
    } catch (err) {
      set({ error: (err as Error).message, videoBusy: false, videoSession: null });
    }
  },

  refreshVideoSession: async (sessionId) => {
    try {
      const session = await apiClient.get<ScrollVideoBrowserSession>(
        `/api/scroll/video-sessions/${sessionId}`,
      );
      if (get().videoSession?.session_id === sessionId) {
        set({ videoSession: session });
      }
    } catch (err) {
      if (get().videoSession?.session_id === sessionId) {
        set({ error: (err as Error).message });
      }
    }
  },

  closeVideoSession: async () => {
    const sessionId = get().videoSession?.session_id;
    if (!sessionId) {
      set({ videoSession: null, videoBusy: false });
      return;
    }
    set({ error: null, videoBusy: true });
    try {
      await apiClient.delete(`/api/scroll/video-sessions/${sessionId}`);
      set({ videoSession: null, videoBusy: false });
    } catch (err) {
      set({ error: (err as Error).message, videoBusy: false });
    }
  },

  videoSessionClick: async (sessionId, x, y) => {
    set({ error: null, videoBusy: true });
    try {
      const session = await apiClient.post<ScrollVideoBrowserSession>(
        `/api/scroll/video-sessions/${sessionId}/click`,
        { x, y },
      );
      set({ videoSession: session, videoBusy: false });
    } catch (err) {
      set({ error: (err as Error).message, videoBusy: false });
    }
  },

  videoSessionScroll: async (sessionId, deltaX, deltaY) => {
    set({ error: null, videoBusy: true });
    try {
      const session = await apiClient.post<ScrollVideoBrowserSession>(
        `/api/scroll/video-sessions/${sessionId}/scroll`,
        { delta_x: deltaX, delta_y: deltaY },
      );
      set({ videoSession: session, videoBusy: false });
    } catch (err) {
      set({ error: (err as Error).message, videoBusy: false });
    }
  },

  clearSourceSession: async (source) => {
    set((state) => ({
      error: null,
      sourceBusy: { ...state.sourceBusy, [source]: true },
    }));
    try {
      const state = await apiClient.delete<ScrollStateResponse>(`/api/scroll/sources/${source}/session`);
        set((current) => ({
          config: state.config,
          sources: state.sources,
          rankingStatus: state.ranking_status,
          items: state.items,
          sourceBusy: { ...current.sourceBusy, [source]: false },
        }));
    } catch (err) {
      set((current) => ({
        error: (err as Error).message,
        sourceBusy: { ...current.sourceBusy, [source]: false },
      }));
    }
  },
}));
